import cn from 'clsx'
import { Fragment } from 'react'
import { Menu, Transition } from '@headlessui/react'
import { FieldError } from 'react-hook-form'
import { ChevronRight } from '@components/icons'

export interface FormDropdownItem {
  label: string
  value: string
  disabled?: boolean
}
export interface FormDropdownProps {
  id?: string
  items: FormDropdownItem[]
  selectedLabel: string | undefined
  placeholder: string
  error?: FieldError
  showPlaceholderOnSelect?: boolean
  onSelect: (index: number, value: string) => void
}

export const FormDropdown = ({
  id,
  items,
  selectedLabel,
  placeholder,
  error,
  onSelect,
  showPlaceholderOnSelect = true,
}: FormDropdownProps) => {
  const shouldShowPlaceholder =
    selectedLabel === undefined || showPlaceholderOnSelect

  return (
    <div className="w-full ">
      <Menu
        id={id}
        as="div"
        className="relative w-full h-16 border-deepPurple text-left"
      >
        {({ open }) => (
          <>
            <div>
              <Menu.Button
                type="button"
                className={cn(
                  'w-full h-16 justify-between flex border border-deepPurple items-center',
                  {
                    'py-0 items-start': selectedLabel,
                  }
                )}
              >
                <p
                  className={cn(
                    'flex flex-col items-start overflow-hidden max-w-full',
                    {
                      'h-full': selectedLabel,
                    }
                  )}
                >
                  <span
                    className={cn(
                      'p-2 whitespace-nowrap text-ellipsis overflow-hidden max-w-full transition-all',
                      {
                        'static p-0 scale-[.75] leading-5': selectedLabel,
                        'opacity-0': !shouldShowPlaceholder,
                        'text-error': error,
                      }
                    )}
                  >
                    {placeholder}
                  </span>
                  <span
                    className={cn(
                      'whitespace-nowrap text-ellipsis overflow-hidden max-w-full max-h-0 transition-all',
                      {
                        'px-3 max-h-6': selectedLabel !== undefined,
                        'text-error': error,
                      }
                    )}
                  >
                    {selectedLabel}
                  </span>
                </p>
                <div className={cn('transition-all', { 'rotate-90': open })}>
                  <ChevronRight stroke={error ? 'red' : 'white'} />
                </div>
              </Menu.Button>
            </div>
            <Transition
              show={open}
              as={Fragment}
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
            >
              <Menu.Items className="absolute flex flex-col max-h-[10.125rem] overflow-y-auto right-0 origin-top-right w-full justify-between z-10 border border-t-0 border-deepPurple">
                {items.map(({ label, value, disabled = false }, index) => (
                  <Menu.Item key={value} disabled={disabled}>
                    {({ active }) => (
                      <button
                        type="button"
                        onClick={() => onSelect(index, value)}
                        className={cn(
                          'group flex items-center min-h-[2.25rem] px-2 border-b border-deepPurple',
                          {
                            'bg-deepPurple': active || label === selectedLabel,
                            'bg-superDark': !active && label !== selectedLabel,
                            'border-t-0': index === 0,
                            'border-b-0': index === items.length - 1,
                          }
                        )}
                      >
                        <span
                          className={cn(
                            'text-left w-full whitespace-nowrap text-ellipsis overflow-hidden',
                            {
                              'text-grey cursor-default': disabled,
                              'text-white': !disabled,
                            }
                          )}
                        >
                          {label}
                        </span>
                      </button>
                    )}
                  </Menu.Item>
                ))}
              </Menu.Items>
            </Transition>
          </>
        )}
      </Menu>
    </div>
  )
}

FormDropdown.displayName = 'FormDropdown'
